import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const REFRESH_MS = 5000;

/**
 * Live admin dashboard.
 * Access is granted by either:
 *   1. Being logged in with an email listed in the ADMIN_EMAILS env var, OR
 *   2. Pasting the ADMIN_TOKEN (stored in localStorage, sent as X-Admin-Token)
 */

const formatUptime = (secs) => {
  if (!secs && secs !== 0) return "—";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h || d) parts.push(`${h}h`);
  parts.push(`${m}m`);
  return parts.join(" ");
};

// How long a visitor has been in a room (or was, if they've left) — "45s", "3m 20s", "1h 12m".
const formatDuration = (secs) => {
  if (secs == null) return "—";
  secs = Math.max(0, Math.round(secs));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m < 60) return s ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
};

// Relative "X ago" for a join/leave timestamp.
const formatTimeAgo = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const StatCard = ({ label, value, sub, accent, testId, onClick }) => (
  <div
    onClick={onClick}
    className={`bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-5 ${
      onClick ? "cursor-pointer hover:bg-black/60 hover:border-white/20 transition-all" : ""
    }`}
    data-testid={testId}
  >
    <div
      className="text-[11px] tracking-[0.25em] font-semibold mb-1"
      style={{ color: accent || "#00F0FF" }}
    >
      {label}
    </div>
    <div
      className="text-5xl tabular-nums text-white leading-none"
      style={{ fontFamily: "Anton, sans-serif" }}
    >
      {value ?? "—"}
    </div>
    {sub && (
      <div className="text-[11px] text-[#A1A1AA] mt-2 font-mono">
        {sub}
        {onClick && <span className="ml-1 text-[#00F0FF]">· view →</span>}
      </div>
    )}
  </div>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [needsToken, setNeedsToken] = useState(false);
  const [tokenDraft, setTokenDraft] = useState("");
  const [lastFetched, setLastFetched] = useState(null);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [busy, setBusy] = useState(null); // 'close-all' | 'close-selected' | roomCode
  const [flash, setFlash] = useState(null);
  const [expandedRoom, setExpandedRoom] = useState(null);
  const [visitorDetail, setVisitorDetail] = useState({}); // room_code -> { loading, visitors, error }
  const [showProUsers, setShowProUsers] = useState(false);
  const [proUsersDetail, setProUsersDetail] = useState(null); // { loading, users, error }
  const intervalRef = useRef(null);

  const savedToken = () => localStorage.getItem("admin_token") || "";

  const authHeaders = () => {
    const t = savedToken();
    return t ? { "X-Admin-Token": t } : {};
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/admin/stats`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setStats(res.data);
      setNeedsToken(false);
      setError(null);
      setLastFetched(new Date());
      // Drop selection entries for rooms that no longer exist
      const alive = new Set((res.data.top_rooms || []).map((r) => r.room_code));
      setSelectedCodes((prev) => prev.filter((c) => alive.has(c)));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setNeedsToken(true);
        setError(null);
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to load stats");
      }
    }
  };

  const closeRooms = async ({ codes = null, all = false }) => {
    const body = all ? { all: true } : { room_codes: codes || [] };
    const label = all ? "close-all" : codes?.length === 1 ? codes[0] : "close-selected";
    setBusy(label);
    setError(null);
    try {
      const res = await axios.post(
        `${backendUrl}/api/admin/close-rooms`,
        body,
        { headers: { ...authHeaders(), "Content-Type": "application/json" }, withCredentials: true }
      );
      setFlash(`Closed ${res.data.closed_count} room${res.data.closed_count === 1 ? "" : "s"}.`);
      setSelectedCodes([]);
      // Refresh immediately so counters update
      await fetchStats();
      setTimeout(() => setFlash(null), 3500);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to close rooms");
    } finally {
      setBusy(null);
    }
  };

  const toggleSelected = (code) => {
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const selectAllVisible = () => {
    setSelectedCodes((stats?.top_rooms || []).map((r) => r.room_code));
  };

  const clearSelection = () => setSelectedCodes([]);

  const fetchVisitors = async (roomCode) => {
    setVisitorDetail((prev) => ({
      ...prev,
      [roomCode]: { ...(prev[roomCode] || {}), loading: true, error: null },
    }));
    try {
      const res = await axios.get(
        `${backendUrl}/api/admin/rooms/${roomCode}/visitors`,
        { headers: authHeaders(), withCredentials: true }
      );
      setVisitorDetail((prev) => ({
        ...prev,
        [roomCode]: { loading: false, visitors: res.data.visitors || [], error: null },
      }));
    } catch (e) {
      setVisitorDetail((prev) => ({
        ...prev,
        [roomCode]: {
          loading: false,
          visitors: [],
          error: e?.response?.data?.detail || e?.message || "Failed to load visitors",
        },
      }));
    }
  };

  const toggleExpandRoom = (roomCode) => {
    setExpandedRoom((prev) => {
      const next = prev === roomCode ? null : roomCode;
      if (next) fetchVisitors(next);
      return next;
    });
  };

  const fetchProUsers = async () => {
    setProUsersDetail((prev) => ({ ...(prev || {}), loading: true, error: null }));
    try {
      const res = await axios.get(`${backendUrl}/api/admin/pro-users`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setProUsersDetail({ loading: false, users: res.data.users || [], error: null });
    } catch (e) {
      setProUsersDetail({
        loading: false,
        users: [],
        error: e?.response?.data?.detail || e?.message || "Failed to load Pro users",
      });
    }
  };

  const toggleProUsers = () => {
    setShowProUsers((prev) => {
      const next = !prev;
      if (next) fetchProUsers();
      return next;
    });
  };

  useEffect(() => {
    fetchStats();
    intervalRef.current = setInterval(fetchStats, REFRESH_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  const applyToken = (e) => {
    e.preventDefault();
    if (!tokenDraft.trim()) return;
    localStorage.setItem("admin_token", tokenDraft.trim());
    setTokenDraft("");
    fetchStats();
  };

  // ----- Not authorized: show token form -----
  if (needsToken) {
    return (
      <div className="min-h-screen w-full bg-gradient-to-br from-[#0F0F14] to-[#18181B] flex items-center justify-center px-4">
        <form
          onSubmit={applyToken}
          className="w-full max-w-sm bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6"
        >
          <h1
            className="text-3xl text-white mb-2"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            ADMIN ACCESS
          </h1>
          <p className="text-[#A1A1AA] text-sm mb-4">
            Sign in with an admin email OR paste the admin token to view live
            stats.
          </p>
          <input
            type="password"
            placeholder="Paste ADMIN_TOKEN"
            value={tokenDraft}
            onChange={(e) => setTokenDraft(e.target.value)}
            className="w-full bg-white/5 border border-white/10 text-white rounded-xl px-4 py-3 mb-3 focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] outline-none font-mono text-sm"
            data-testid="admin-token-input"
          />
          <button
            type="submit"
            className="w-full py-3 bg-[#00F0FF] text-black font-bold rounded-xl hover:bg-[#33F3FF] transition-all active:scale-95"
            data-testid="admin-token-submit"
          >
            Unlock dashboard
          </button>
          <Link
            to="/"
            className="block text-center text-xs text-[#A1A1AA] mt-4 hover:text-white transition"
          >
            ← Back to app
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen w-full bg-gradient-to-br from-[#0F0F14] to-[#18181B] px-4 sm:px-8 py-8"
      data-testid="admin-dashboard"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6">
        <div>
          <div className="text-[11px] tracking-[0.3em] text-[#00F0FF] font-semibold mb-1">
            TAP FIRST · ADMIN
          </div>
          <h1
            className="text-5xl sm:text-6xl text-white"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            LIVE STATS
          </h1>
          <p className="text-[#A1A1AA] text-xs mt-1">
            Auto-refreshes every {REFRESH_MS / 1000}s ·{" "}
            {lastFetched && (
              <span data-testid="admin-last-updated">
                Last update {lastFetched.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2 items-center">
          <button
            onClick={fetchStats}
            className="px-3 py-1.5 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-full hover:bg-white/20 transition-all"
            data-testid="admin-refresh-button"
          >
            ↻ Refresh
          </button>
          {savedToken() && (
            <button
              onClick={() => {
                localStorage.removeItem("admin_token");
                setStats(null);
                setNeedsToken(true);
              }}
              className="px-3 py-1.5 bg-[#FF0055]/15 text-[#FF0055] border border-[#FF0055]/30 text-xs font-semibold rounded-full hover:bg-[#FF0055]/25 transition-all"
              data-testid="admin-logout-token"
            >
              Log out
            </button>
          )}
          <Link
            to="/"
            className="px-3 py-1.5 text-[#A1A1AA] text-xs hover:text-white transition"
          >
            ← Back
          </Link>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 px-4 py-2 rounded-lg bg-[#FF0055]/15 border border-[#FF0055]/30 text-[#FF0055] text-sm"
          data-testid="admin-error"
        >
          {error}
        </div>
      )}

      {flash && (
        <div
          className="mb-4 px-4 py-2 rounded-lg bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-sm"
          data-testid="admin-flash"
        >
          {flash}
        </div>
      )}

      {/* Live counters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <StatCard
          testId="stat-connected-sockets"
          label="CONNECTED NOW"
          value={stats?.connected_sockets}
          sub="Live websocket clients"
          accent="#00F0FF"
        />
        <StatCard
          testId="stat-active-participants"
          label="IN A ROOM"
          value={stats?.active_participants}
          sub="Actively playing / lobbies"
          accent="#FFEA00"
        />
        <StatCard
          testId="stat-active-rooms"
          label="ACTIVE ROOMS"
          value={stats?.active_rooms}
          sub={
            stats
              ? `${stats.lobbies || 0} lobbies · ${stats.playing_rooms || 0} playing`
              : ""
          }
          accent="#FF0055"
        />
        <StatCard
          testId="stat-total-users"
          label="TOTAL USERS"
          value={stats?.total_users}
          sub={
            stats?.signups_24h != null
              ? `+${stats.signups_24h} in last 24h`
              : ""
          }
          accent="#9D4EDD"
        />
        <StatCard
          testId="stat-pro-users"
          label="PRO USERS"
          value={stats?.pro_users}
          sub={
            stats?.pro_users != null && stats?.total_users
              ? `${((stats.pro_users / stats.total_users) * 100).toFixed(1)}% of all users`
              : ""
          }
          accent="#FFEA00"
          onClick={toggleProUsers}
        />
        <StatCard
          testId="stat-rooms-24h"
          label="ROOMS · 24H"
          value={stats?.rooms_created_24h}
          sub={`Uptime ${formatUptime(stats?.uptime_seconds)}`}
          accent="#00F0FF"
        />
      </div>

      {/* Pro users panel — toggled open from the PRO USERS stat card above */}
      {showProUsers && (
        <div
          className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden mb-4"
          data-testid="admin-pro-users-panel"
        >
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <h2
              className="text-white text-lg tracking-wide"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              PRO USERS
            </h2>
            <button
              onClick={() => setShowProUsers(false)}
              className="text-xs text-[#A1A1AA] hover:text-white transition"
              data-testid="admin-close-pro-users"
            >
              Close
            </button>
          </div>
          <div className="px-5 py-4">
            {!proUsersDetail || proUsersDetail.loading ? (
              <div className="text-xs text-[#A1A1AA]">Loading Pro users…</div>
            ) : proUsersDetail.error ? (
              <div className="text-xs text-[#FF0055]">{proUsersDetail.error}</div>
            ) : proUsersDetail.users.length === 0 ? (
              <div className="text-xs text-[#A1A1AA]">No Pro users yet.</div>
            ) : (
              <div className="space-y-1.5">
                <div className="grid grid-cols-[1.6fr_1fr_auto] gap-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]/70 px-1">
                  <div>Name / Email</div>
                  <div>Tier / Source</div>
                  <div className="text-right">Upgraded</div>
                </div>
                {proUsersDetail.users.map((u, idx) => (
                  <div
                    key={u.email || idx}
                    className="grid grid-cols-[1.6fr_1fr_auto] gap-2 text-xs px-1 py-2 rounded-lg odd:bg-white/[0.02]"
                    data-testid={`pro-user-${idx}`}
                  >
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{u.name || "—"}</div>
                      <div className="text-[#A1A1AA] truncate">{u.email}</div>
                    </div>
                    <div className="text-[#A1A1AA] uppercase whitespace-nowrap">
                      {u.pro_tier || "—"}
                      {u.pro_source ? ` · ${u.pro_source}` : ""}
                    </div>
                    <div className="text-right text-[#A1A1AA] whitespace-nowrap">
                      {formatTimeAgo(u.upgraded_at)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rooms table w/ close controls */}
      <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden">
        {/* Table header + bulk actions */}
        <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <h2
              className="text-white text-lg tracking-wide"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              LIVE ROOMS
            </h2>
            <span className="text-xs text-[#A1A1AA]">
              {stats?.top_rooms?.length || 0} shown
              {selectedCodes.length > 0 && (
                <span className="ml-2 text-[#00F0FF]">
                  · {selectedCodes.length} selected
                </span>
              )}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedCodes.length > 0 ? (
              <>
                <button
                  onClick={clearSelection}
                  className="px-3 py-1.5 text-xs text-[#A1A1AA] hover:text-white transition"
                  data-testid="admin-clear-selection"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Close ${selectedCodes.length} selected room${
                          selectedCodes.length === 1 ? "" : "s"
                        }? All players in them will be disconnected.`
                      )
                    ) {
                      closeRooms({ codes: selectedCodes });
                    }
                  }}
                  disabled={busy === "close-selected"}
                  className="px-3 py-1.5 bg-[#FF0055]/25 text-[#FF0055] border border-[#FF0055]/50 text-xs font-bold rounded-full hover:bg-[#FF0055]/40 transition-all active:scale-95 disabled:opacity-50"
                  data-testid="admin-close-selected"
                >
                  {busy === "close-selected"
                    ? "Closing…"
                    : `Close ${selectedCodes.length} selected`}
                </button>
              </>
            ) : (
              <button
                onClick={selectAllVisible}
                className="px-3 py-1.5 bg-white/10 border border-white/20 text-white text-xs font-semibold rounded-full hover:bg-white/20 transition-all"
                data-testid="admin-select-all-visible"
              >
                Select all visible
              </button>
            )}
            <button
              onClick={() => {
                if (
                  window.confirm(
                    `Close ALL ${stats?.active_rooms || 0} rooms? This cannot be undone. Every connected player will be kicked.`
                  )
                ) {
                  closeRooms({ all: true });
                }
              }}
              disabled={busy === "close-all" || !stats?.active_rooms}
              className="px-3 py-1.5 bg-[#FF0055] text-white text-xs font-bold rounded-full hover:bg-[#FF3377] transition-all active:scale-95 disabled:opacity-40 shadow-[0_0_10px_rgba(255,0,85,0.4)]"
              data-testid="admin-close-all"
            >
              {busy === "close-all" ? "Closing…" : "🗑 Close ALL rooms"}
            </button>
          </div>
        </div>

        {stats?.top_rooms?.length ? (
          <>
            <div className="px-5 py-2 border-b border-white/10 grid grid-cols-[auto_1.6fr_1.8fr_auto_auto_auto_auto_auto_auto] gap-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]/70">
              <div></div>
              <div>Room</div>
              <div>Host</div>
              <div>State</div>
              <div className="text-center">Rnd</div>
              <div className="text-center">Visits</div>
              <div className="text-center">Avg time</div>
              <div className="text-right">Now</div>
              <div></div>
            </div>
            <div className="divide-y divide-white/5">
            {stats.top_rooms.map((r) => {
              const checked = selectedCodes.includes(r.room_code);
              const closingThis = busy === r.room_code;
              const expanded = expandedRoom === r.room_code;
              const detail = visitorDetail[r.room_code];
              return (
                <div key={r.room_code}>
                  <div
                    className={`px-5 py-3 grid grid-cols-[auto_1.6fr_1.8fr_auto_auto_auto_auto_auto_auto] gap-2 items-center transition-colors ${
                      checked ? "bg-[#00F0FF]/5" : ""
                    }`}
                    data-testid={`top-room-${r.room_code}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelected(r.room_code)}
                      className="w-4 h-4 accent-[#00F0FF] cursor-pointer"
                      data-testid={`room-checkbox-${r.room_code}`}
                    />
                    <button
                      onClick={() => toggleExpandRoom(r.room_code)}
                      className="flex items-center gap-1.5 font-mono text-[#00F0FF] font-bold tracking-wider truncate text-left hover:underline"
                      data-testid={`room-expand-${r.room_code}`}
                    >
                      <span className="text-[10px] text-[#A1A1AA]">{expanded ? "▾" : "▸"}</span>
                      {r.room_code}
                    </button>
                    <div className="text-white truncate">{r.host_name}</div>
                    <div className="text-xs">
                      <span
                        className={`px-2 py-0.5 rounded-full whitespace-nowrap ${
                          r.game_state === "playing"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : "bg-white/10 text-[#A1A1AA]"
                        }`}
                      >
                        {r.game_state}
                      </span>
                    </div>
                    <div className="text-xs text-[#A1A1AA] text-center">R{r.round}</div>
                    <div className="text-center">
                      <span className="text-white text-sm tabular-nums font-bold">
                        {r.visit_count ?? 0}
                      </span>
                    </div>
                    <div className="text-xs text-[#A1A1AA] text-center whitespace-nowrap">
                      {formatDuration(r.avg_seconds)}
                    </div>
                    <div className="text-right">
                      <span className="text-white text-base tabular-nums font-bold">
                        {r.participants}
                      </span>
                    </div>
                    <div className="text-right">
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              `Close room ${r.room_code}? ${r.participants} player${
                                r.participants === 1 ? "" : "s"
                              } will be disconnected.`
                            )
                          ) {
                            closeRooms({ codes: [r.room_code] });
                          }
                        }}
                        disabled={closingThis}
                        className="px-3 py-1 bg-[#FF0055]/15 text-[#FF0055] border border-[#FF0055]/30 text-xs font-bold rounded-full hover:bg-[#FF0055]/25 transition-all active:scale-95 disabled:opacity-50 whitespace-nowrap"
                        data-testid={`close-room-${r.room_code}`}
                      >
                        {closingThis ? "…" : "Close"}
                      </button>
                    </div>
                  </div>
                  {expanded && (
                    <div
                      className="px-5 py-3 bg-white/[0.03] border-t border-white/5"
                      data-testid={`room-visitors-${r.room_code}`}
                    >
                      {!detail || detail.loading ? (
                        <div className="text-xs text-[#A1A1AA]">Loading visitors…</div>
                      ) : detail.error ? (
                        <div className="text-xs text-[#FF0055]">{detail.error}</div>
                      ) : detail.visitors.length === 0 ? (
                        <div className="text-xs text-[#A1A1AA]">No recorded visits for this room yet.</div>
                      ) : (
                        <div className="space-y-1.5">
                          <div className="grid grid-cols-[1.4fr_auto_auto_auto] gap-2 text-[10px] uppercase tracking-wider text-[#A1A1AA]/70 px-1">
                            <div>Name</div>
                            <div>Joined</div>
                            <div>Time spent</div>
                            <div className="text-right">Status</div>
                          </div>
                          {detail.visitors.map((v, idx) => (
                            <div
                              key={`${v.participant_name}-${idx}`}
                              className="grid grid-cols-[1.4fr_auto_auto_auto] gap-2 text-xs px-1 py-1 rounded-lg odd:bg-white/[0.02]"
                            >
                              <div className="text-white truncate">
                                {v.participant_name}
                                {v.is_host && (
                                  <span className="ml-1.5 text-[9px] text-[#9D4EDD]">HOST</span>
                                )}
                              </div>
                              <div className="text-[#A1A1AA] whitespace-nowrap">
                                {formatTimeAgo(v.joined_at)}
                              </div>
                              <div className="text-[#A1A1AA] whitespace-nowrap">
                                {formatDuration(v.duration_seconds)}
                              </div>
                              <div className="text-right whitespace-nowrap">
                                {v.currently_connected ? (
                                  <span className="text-emerald-300">● online</span>
                                ) : (
                                  <span className="text-[#71717A]">offline</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            </div>
          </>
        ) : (
          <div className="px-5 py-8 text-center text-[#A1A1AA] text-sm">
            No live rooms right now.
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
